import React, { useEffect, useMemo, useState } from "react"

import { useForm } from "react-hook-form"

import { FRONT_REGEX_CONFIG } from "@front-shared/config/regex.config"
import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { CheckboxElement } from "@front-shared/elements/checkbox.element"
import { InputElement } from "@front-shared/elements/input.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import type { IBulkResultsSmartScanPreparationForm } from "../../../types/bulkResultsInsights.types"
import type { IDynamicCheckerFormInputData } from "../../../types/data.types"

export const BulkResultsSmartScanPreparationComponent: React.FunctionComponent<
    IBulkResultsSmartScanPreparationComponentProps
> = props => {
    const {
        control,
        watch,
        setValue,
        getValues,
        trigger,
        formState: { errors },
    } = useForm<IBulkResultsSmartScanPreparationForm>({
        mode: "onChange",
        defaultValues: {
            bulkZip: "",
            zipCodes: Object.fromEntries(
                props.inputData.map(inputData => [
                    inputData.id,
                    inputData.zipCode || "",
                ]),
            ),
        },
    })

    const bulkZip = watch("bulkZip") || ""
    const zipCodes = watch("zipCodes") || {}

    // Absent means selected. Only an explicit un-tick writes a key, so rows that
    // arrive after mount are checked by default without needing a back-fill
    const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>(
        {},
    )

    const selectedInputData = useMemo(
        () =>
            props.inputData.filter(
                inputData => selectedRows[inputData.id] ?? true,
            ),
        [props.inputData, selectedRows],
    )

    // Not memoized: watch returns the live form values object, so its identity
    // stays the same while ZIPs are edited and a memo would never recompute
    const validCount = selectedInputData.filter(inputData =>
        FRONT_REGEX_CONFIG.zip.test(
            zipCodes[inputData.id] ?? inputData.zipCode ?? "",
        ),
    ).length

    const invalidCount = selectedInputData.length - validCount
    const isBulkZipValid = FRONT_REGEX_CONFIG.zip.test(bulkZip)

    // useForm seeds defaultValues once, so rows that arrive after mount need
    // their ZIP registered here
    useEffect(() => {
        const currentZipCodes = getValues("zipCodes") || {}

        props.inputData.forEach(inputData => {
            if (currentZipCodes[inputData.id] !== undefined) {
                return
            }

            setValue(`zipCodes.${inputData.id}`, inputData.zipCode || "", {
                shouldValidate: true,
            })
        })
    }, [getValues, props.inputData, setValue])

    /**
     * Apply the bulk ZIP only to rows that do not already contain a valid ZIP
     */
    const onApplyToMissing = () => {
        if (!isBulkZipValid) {
            return
        }

        props.inputData.forEach(inputData => {
            const isSelected = selectedRows[inputData.id] ?? true

            const currentZip =
                getValues(`zipCodes.${inputData.id}`) ?? inputData.zipCode ?? ""

            if (!isSelected || FRONT_REGEX_CONFIG.zip.test(currentZip)) {
                return
            }

            setValue(`zipCodes.${inputData.id}`, bulkZip, {
                shouldValidate: true,
            })
        })
    }

    /**
     * Replace the ZIP on every selected row with the entered bulk ZIP
     */
    const onApplyToAll = () => {
        if (!isBulkZipValid) {
            return
        }

        props.inputData.forEach(inputData => {
            if (!(selectedRows[inputData.id] ?? true)) {
                return
            }

            setValue(`zipCodes.${inputData.id}`, bulkZip, {
                shouldValidate: true,
            })
        })
    }

    /**
     * Submit cloned input rows containing the reviewed ZIP values
     */
    const onRunSmartScan = async () => {
        if (!selectedInputData.length) {
            return
        }

        const isValid = await trigger(
            selectedInputData.map(
                inputData => `zipCodes.${inputData.id}` as const,
            ),
        )

        if (!isValid) {
            return
        }

        const formZipCodes = getValues("zipCodes")

        props.onRun(
            selectedInputData.map(inputData => ({
                ...inputData,
                zipCode: formZipCodes[inputData.id] ?? inputData.zipCode ?? "",
            })),
        )
    }

    return (
        <div className="mt-[16px] rounded-[16px] border border-solid border-brand-warmLight2 bg-brand-white p-[24px] shadow-sm">
            <div className="mb-[20px]">
                <PrimaryText typography="h5">Prepare SmartScan</PrimaryText>

                <PrimaryText className="mt-[4px] opacity-60">
                    Review the member ZIP for each inactive policy before
                    running SmartScan.
                </PrimaryText>
            </div>

            <div className="mb-[24px] flex flex-wrap items-end gap-[12px] rounded-[12px] bg-brand-warmLight025 p-[16px]">
                <InputElement
                    className="min-w-[220px] flex-1"
                    name="bulkZip"
                    label="Apply one ZIP to multiple patients"
                    isLabelStatic
                    placeholder="10000"
                    reactHookControl={control as any}
                    reactHookErrors={errors}
                    reactHookValidations={{
                        pattern: FRONT_VALIDATION_CONFIG.zip,
                    }}
                    shouldUnregister={false}
                />

                <ButtonElement
                    type="default"
                    htmlType="button"
                    size="large"
                    label="Apply to missing ZIPs"
                    disabled={!isBulkZipValid}
                    onClick={onApplyToMissing}
                />

                <ButtonElement
                    type="default"
                    htmlType="button"
                    size="large"
                    label="Replace all ZIPs"
                    disabled={!isBulkZipValid}
                    onClick={onApplyToAll}
                />
            </div>

            <div className="overflow-x-auto">
                <div className="min-w-[620px]">
                    <div className="grid grid-cols-[1.4fr_1.2fr_180px] gap-[16px] border-0 border-b border-solid border-brand-warmLight2 px-[8px] py-[8px]">
                        {["Patient", "Payer", "Member ZIP"].map(header => (
                            <PrimaryText
                                key={header}
                                typography="subtitle"
                                className="uppercase opacity-50"
                            >
                                {header}
                            </PrimaryText>
                        ))}
                    </div>

                    {props.inputData.map(inputData => {
                        const isSelected = selectedRows[inputData.id] ?? true

                        return (
                            <div
                                key={inputData.id}
                                className="grid grid-cols-[1.4fr_1.2fr_180px] items-center gap-[16px] border-0 border-b border-solid border-brand-warmLight2 px-[8px] py-[10px]"
                            >
                                <div>
                                    <CheckboxElement
                                        name={`smartScan-${inputData.id}`}
                                        defaultChecked={isSelected}
                                        watchDefaultCheckedChange
                                        label={
                                            `${inputData.firstName || ""} ${
                                                inputData.lastName || ""
                                            }`.trim() || "—"
                                        }
                                        onChange={isChecked => {
                                            setSelectedRows(current => ({
                                                ...current,
                                                [inputData.id]: isChecked,
                                            }))
                                        }}
                                    />

                                    {inputData.memberId ? (
                                        <PrimaryText
                                            typography="subtitle"
                                            className="mt-[2px] opacity-60"
                                        >
                                            {inputData.memberId}
                                        </PrimaryText>
                                    ) : null}
                                </div>

                                <PrimaryText>
                                    {inputData.payer?.insuranceName || "—"}
                                </PrimaryText>

                                <InputElement
                                    name={inputData.id}
                                    fieldGroupName="zipCodes"
                                    placeholder="10000"
                                    reactHookControl={control as any}
                                    reactHookErrors={errors.zipCodes}
                                    reactHookValidations={
                                        isSelected
                                            ? {
                                                  required:
                                                      FRONT_VALIDATION_CONFIG.required,
                                                  pattern:
                                                      FRONT_VALIDATION_CONFIG.zip,
                                              }
                                            : undefined
                                    }
                                    defaultValue={
                                        zipCodes[inputData.id] ??
                                        inputData.zipCode ??
                                        ""
                                    }
                                    disabled={!isSelected}
                                    shouldUnregister={false}
                                />
                            </div>
                        )
                    })}
                </div>
            </div>

            <div className="mt-[20px] flex flex-wrap items-center justify-between gap-[16px]">
                <PrimaryText>
                    <span className="text-bold">{validCount} ready</span>
                    {invalidCount
                        ? ` · ${invalidCount} need a valid ZIP`
                        : selectedInputData.length
                          ? " · all selected ZIPs are valid"
                          : " · select at least one patient"}
                </PrimaryText>

                <div className="flex items-center gap-[12px]">
                    <ButtonElement
                        type="default"
                        htmlType="button"
                        size="large"
                        label="Cancel"
                        onClick={props.onCancel}
                    />

                    <ButtonElement
                        type="primary"
                        htmlType="button"
                        size="large"
                        label={`Run SmartScan on ${selectedInputData.length} patient${
                            selectedInputData.length === 1 ? "" : "s"
                        }`}
                        disabled={!!invalidCount || !selectedInputData.length}
                        onClick={onRunSmartScan}
                    />
                </div>
            </div>
        </div>
    )
}

interface IBulkResultsSmartScanPreparationComponentProps {
    inputData: IDynamicCheckerFormInputData[]
    onCancel: () => void
    onRun: (inputData: IDynamicCheckerFormInputData[]) => void
}
